import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { formatDay, usePrefs } from '../lib/prefs.tsx';

/**
 * Who, outside this company, can see this vehicle.
 *
 * Owners only, and it lives on the vehicle rather than in People because that
 * is where somebody stands when they decide it: looking at a car, knowing
 * whose it is. The People tab lists staff, and a customer is not staff - they
 * are in the customers company and never appear there.
 *
 * Granting was possible over the API from the day the customer role shipped,
 * and there was no way to do it or to see who already had access. An owner
 * cannot manage what they cannot see.
 */
export function VehicleAccess({ vin }: { vin: string }) {
  const { dateFormat } = usePrefs();
  const [granted, setGranted] = useState<
    { id: number; email: string; name: string | null; granted_at: string; last_login_at: string | null }[] | null
  >(null);
  const [pending, setPending] = useState<{ email: string; created_at: string; expires_at: string | null }[]>([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = () =>
    api
      .vehicleAccess(vin)
      .then((d) => {
        setGranted(d.granted);
        setPending(d.pending);
      })
      // A non-owner gets a 403 here, which is not an error worth showing them.
      .catch(() => setGranted(null));

  useEffect(() => {
    load();
  }, [vin]);

  if (granted === null) return null;

  // `after` is optional because the grant path writes its own message from the
  // response - whether they were invited or granted is only known once it
  // returns, and overwriting it here would throw that away.
  const act = async (fn: () => Promise<unknown>, after?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (after) setNotice(after);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" id="veh-access">
      <h2>Owner access</h2>
      <p className="small dim">
        A customer account granted this vehicle sees this car and nothing else of yours. Access follows the car, so
        the same person can be given vehicles held at more than one company.
      </p>

      {error && <div className="banner error small" style={{ marginBottom: 10 }}>{error}</div>}
      {notice && <div className="banner small" style={{ marginBottom: 10 }}>{notice}</div>}

      {granted.length === 0 && pending.length === 0 ? (
        <p className="small dim">Nobody outside this company can see this vehicle.</p>
      ) : (
        <div className="people-list">
          {granted.map((g) => (
            <div key={g.id} className="person-row">
              <div className="person-main">
                <span className="person-name">{g.name || g.email}</span>
                <span className="small dim mono">{g.email}</span>
                {/* When it was shared, and whether they have ever come to look.
                    Shared in March and never signed in is a different situation
                    from shared yesterday, and only the dates tell them apart. */}
                <span className="small dim">
                  Shared {formatDay(g.granted_at, dateFormat)}
                  {g.last_login_at ? ` · last signed in ${formatDay(g.last_login_at, dateFormat)}` : ' · never signed in'}
                </span>
              </div>
              <div className="person-actions">
                <button
                  className="tiny ghost"
                  disabled={busy}
                  onClick={() => act(() => api.grantVehicle(vin, g.email, true), `${g.email} can no longer see it.`)}
                >
                  remove access
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invited but not yet registered. Shown beside the real grants because
          an owner who cannot see them will invite the same person twice. */}
      {pending.length > 0 && (
        <div className="people-list" style={{ marginTop: granted.length ? 8 : 0 }}>
          {pending.map((p) => {
            const lapsed = p.expires_at ? new Date(p.expires_at).getTime() < Date.now() : false;
            return (
              <div key={p.email} className="person-row">
                <div className="person-main">
                  <span className="person-name">
                    {p.email}
                    <span className={`pill tiny-pill ${lapsed ? 'crit' : 'warn'}`}>{lapsed ? 'expired' : 'invited'}</span>
                  </span>
                  <span className="small dim">
                    Sent {formatDay(p.created_at, dateFormat)}
                    {p.expires_at
                      ? lapsed
                        ? ` · expired ${formatDay(p.expires_at, dateFormat)}`
                        : ` · expires ${formatDay(p.expires_at, dateFormat)}`
                      : ''}
                  </span>
                </div>
                <div className="person-actions">
                  <button
                    className="tiny ghost"
                    disabled={busy}
                    onClick={() =>
                      act(async () => {
                        const r = await api.resendVehicleInvite(vin, p.email);
                        setNotice(
                          r.mailSent
                            ? `A fresh invitation has gone to ${r.email}, with a new code and a new week to use it.`
                            : `${r.notice ?? 'Mail is not configured.'} Pass this code to ${r.email}: ${r.code}`,
                        );
                      })
                    }
                  >
                    send again
                  </button>
                  <button
                    className="tiny ghost"
                    disabled={busy}
                    onClick={() => act(() => api.grantVehicle(vin, p.email, true), `Invitation to ${p.email} withdrawn.`)}
                  >
                    withdraw
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <form
        className="row wrap"
        style={{ gap: 8, alignItems: 'flex-end', marginTop: 12 }}
        onSubmit={(e) => {
          e.preventDefault();
          act(async () => {
            const r = await api.grantVehicle(vin, email);
            setEmail('');
            setNotice(
              r.invited
                ? r.mailSent
                  ? `${r.email} has been invited. They will see this vehicle once they set up their account.`
                  : `${r.notice ?? 'Mail is not configured.'} Pass this code to ${r.email} yourself: ${r.code}`
                : r.mailSent
                  ? `${r.email} can now see this vehicle, and has been emailed about it.`
                  : `${r.email} can now see this vehicle. ${r.notice ?? ''}`,
            );
          });
        }}
      >
        <label style={{ flex: '1 1 220px' }}>
          <span>Give access to</span>
          <input
            type="email"
            required
            value={email}
            placeholder="their@email.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <button type="submit" disabled={busy || !email.trim()}>
          Grant
        </button>
      </form>
      {/* The API refuses an address that is not a customer account, which is
          the common mistake: inviting somebody as a customer comes first. */}
      <p className="small dim" style={{ marginTop: 8 }}>
        No account needed. If they do not have one they are invited as a customer and get this vehicle when they
        finish setting up; if they do, they get it now and an email saying so.
      </p>
    </div>
  );
}
